import React from 'react';
import { DateUtil } from '@/utils/dateUtil.ts';
import { PrintUtil } from '@/utils/printUtil.ts';
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

// ── Types ───────────────────────────────────────────────────────────────────

interface InvoiceItem {
  service_name?: string;
  name?: string;
  category?: string;
  quantity?: number;
  unit_price?: number;
  price?: number;
}

export interface InvoiceData {
  // IDs — used by UnifiedPrintModal to fetch patient/doctor/clinic info
  patient_id?: string;
  doctor_id?:  string;
  clinic_id?:  string;

  // Joined fields from backend SELECT — used directly when API fetch not possible
  doctor_name?:    string;
  patient_name?:   string;
  patient_phone?:  string;
  patient_mrn?:    string;
  clinic_name?:    string;
  clinic_address?: string;

  invoice_number?: string;
  invoice_date?:   string;
  visit_date?:     string;
  invoice_status?: string;

  subtotal?:        number;
  discount_amount?: number;
  tax_amount?:      number;
  total_amount?:    number;
  balance_due?:     number;  // from patient_ledger via API — never computed here
  paid_amount?:     number;  // canonical name — no paidAmount alias
  payment_mode?:    string;
  notes?:           string;
  items?:           InvoiceItem[];
}

interface ClinicInfo  { name?: string; address?: string; phone?: string; gstin?: string; }
interface DoctorInfo  { name?: string; }
interface PatientInfo { name?: string; patient_name?: string; mrn?: string; phone?: string; dob?: string; gender?: string; age?: number; }
interface Settings    { profile?: DoctorInfo; }
interface CurrentUser { name?: string; }

export interface InvoiceContentProps {
  data:         InvoiceData | null;
  translations: Record<string, string>;
  language?:    string;
  clinicInfo?:  ClinicInfo  | null;
  doctorInfo?:  DoctorInfo  | null;
  patientInfo?: PatientInfo | null;
  settings?:    Settings    | null;
  currentUser?: CurrentUser | null;
  headerConfig?: HeaderConfig;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (amount?: number | null): string => `₹${Number(amount ?? 0).toFixed(2)}`;
const n   = (v?: number | null): number => Number(v ?? 0);

// ── Component ────────────────────────────────────────────────────────────────

export function InvoiceContent({
                                 data, translations: t,
                                 clinicInfo, doctorInfo, patientInfo, settings, currentUser, headerConfig,
                               }: InvoiceContentProps) {

  if (!data) return null;

  const clinicGstin   = clinicInfo?.gstin;  // for future GST line if needed
  const doctorName    = settings?.profile?.name   ?? doctorInfo?.name    ?? data.doctor_name;

  // Patient: from fetched patientInfo → joined fields on data
  const pName  = patientInfo?.name    ?? patientInfo?.patient_name ?? data.patient_name  ?? '—';
  const pMrn   = patientInfo?.mrn     ?? data.patient_mrn  ?? '—';
  const pPhone = patientInfo?.phone   ?? data.patient_phone ?? '—';

  const status      = data.invoice_status?.toUpperCase();
  const isCancelled = status === 'CANCELLED';
  const isDraft     = status === 'DRAFT';

  const invoiceNumber = data.invoice_number ?? (isDraft ? 'DRAFT' : '—');
  const consultantLabel = doctorName ? `Dr. ${doctorName}` : '—';
  const invoiceDate   = data.invoice_date;
  const visitDate     = data.visit_date ?? data.invoice_date;
  const printedOn     = new Date().toISOString();
  const docTitle      = n(data.paid_amount) > 0 ? 'Bill-cum-Receipt' : 'Invoice';

  return (
      <div style={{ position: 'relative' }}>

        {/* Watermark */}
        {(isCancelled || isDraft) && (
            <div style={{
              position: 'absolute', top: '40%', left: '50%',
              transform: 'translate(-50%, -50%) rotate(-30deg)',
              fontSize: '48px', fontWeight: 'bold', opacity: 0.12,
              color: isCancelled ? '#dc2626' : '#f59e0b',
              pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10,
            }}>
              {isCancelled ? 'CANCELLED' : 'DRAFT'}
            </div>
        )}

        {/* Clinic header (configurable) */}
        {(!headerConfig || headerConfig.mode === 'auto') && clinicInfo?.name && (
            <div style={{ textAlign: 'center', marginBottom: '4mm', borderBottom: '2px solid #000', paddingBottom: '3mm' }}>
              {(!headerConfig || headerConfig.showName) && (
                  <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1mm' }}>{clinicInfo.name}</div>
              )}
              {(!headerConfig || headerConfig.showAddress) && clinicInfo.address && (
                  <div style={{ fontSize: '10px', color: '#444', marginBottom: '1mm' }}>{clinicInfo.address}</div>
              )}
              {(!headerConfig || headerConfig.showPhone) && clinicInfo.phone && (
                  <div style={{ fontSize: '10px', color: '#444' }}>Ph: {clinicInfo.phone}</div>
              )}
            </div>
        )}
        {headerConfig?.mode === 'none' && (
            <div style={{ height: `${headerConfig.topSpaceMm}mm` }} />
        )}
        {headerConfig?.mode === 'custom' && headerConfig.customText && (
            <div style={{ textAlign: 'center', marginBottom: '4mm', borderBottom: '2px solid #000', paddingBottom: '3mm', whiteSpace: 'pre-wrap' }}>
              {headerConfig.customText}
            </div>
        )}

        {/* Document title */}
        <div style={{ textAlign: 'center', marginBottom: '6mm', fontSize: '14px', fontWeight: 'bold' }}>
          {docTitle}
        </div>

        {/* Invoice + Patient info */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10mm', marginBottom: '8mm', fontSize: '11px' }}>
          <div>
            <Row label="MRN"          value={pMrn} />
            <Row label="Invoice No"   value={invoiceNumber} />
            <Row label="Invoice Date" value={invoiceDate ? DateUtil.formatDateForType(invoiceDate, 'date-only') : '—'} />
            <Row label="Consultant"   value={consultantLabel} />
          </div>
          <div>
            <Row label="Patient"    value={PrintUtil.toUpperCaseText(pName) ?? '—'} />
            <Row label="Age/Gender" value={PrintUtil.generateAgeGenderString(patientInfo) ?? '—'} />
            <Row label="Phone"      value={pPhone} />
            <Row label="Visit Date" value={visitDate ? DateUtil.formatDateForType(visitDate, 'date-only') : '—'} />
          </div>
        </div>

        {/* Line items */}
        {!!data.items?.length && (
            <div style={{ marginBottom: '8mm' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '3mm', fontSize: '12px' }}>
                {t.service} &amp; {t.charges}
              </div>
              <div style={{
                display: 'grid', gridTemplateColumns: '6% 40% 15% 10% 14% 15%',
                backgroundColor: '#f8f9fa', border: '1px solid #000',
                padding: '3mm', fontSize: '10px', fontWeight: 'bold',
              }}>
                {['#', t.service, t.category, t.quantity, t.unitPrice, t.totalAmount].map((h, i) => (
                    <div key={i}>{h}</div>
                ))}
              </div>
              {data.items.map((item, idx) => {
                const qty   = Number(item.quantity ?? 1);
                const price = item.unit_price ?? item.price ?? 0;
                return (
                    <div key={idx} style={{
                      display: 'grid', gridTemplateColumns: '6% 40% 15% 10% 14% 15%',
                      border: '1px solid #ccc', borderTop: 'none', padding: '2mm', fontSize: '10px',
                    }}>
                      <div>{idx + 1}</div>
                      <div style={{ fontWeight: 'bold' }}>{item.service_name ?? item.name ?? '—'}</div>
                      <div style={{ textTransform: 'capitalize' }}>{item.category ?? 'Service'}</div>
                      <div>{Number.isInteger(qty) ? qty : qty.toFixed(2)}</div>
                      <div>{fmt(price)}</div>
                      <div style={{ fontWeight: 'bold' }}>{fmt(qty * price)}</div>
                    </div>
                );
              })}
            </div>
        )}

        {/* Notes + Amount summary */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: '8mm', marginBottom: '8mm' }}>
          <div style={{ fontSize: '11px' }}>
            {data.payment_mode && (
                <div style={{ marginBottom: '2mm' }}>
                  <span style={{ fontWeight: 'bold' }}>Payment Mode: </span>{data.payment_mode}
                </div>
            )}
            {data.notes && (
                <div>
                  <span style={{ fontWeight: 'bold' }}>Notes: </span>
                  <span style={{ color: '#555' }}>{data.notes}</span>
                </div>
            )}
          </div>
          <div style={{ fontSize: '11px', padding: '3mm' }}>
            <AmtRow label={t.subtotal ?? 'Subtotal'} value={fmt(data.subtotal ?? data.total_amount)} />
            {n(data.discount_amount) > 0 && (
                <AmtRow label="Discount" value={`- ${fmt(data.discount_amount)}`} />
            )}
            {n(data.tax_amount) > 0 && (
                <AmtRow label="Tax" value={fmt(data.tax_amount)} />
            )}
            <div style={{
              borderTop: '1px solid #000', paddingTop: '2mm', marginTop: '2mm',
              display: 'flex', justifyContent: 'space-between', fontSize: '12px', fontWeight: 'bold',
            }}>
              <span>{t.total ?? 'Total'}:</span>
              <span>{fmt(data.total_amount)}</span>
            </div>
            {n(data.paid_amount) > 0 && (
                <AmtRow label={t.amountPaid ?? 'Amount Paid'} value={fmt(data.paid_amount)} color="#16a34a" />
            )}
            {n(data.balance_due) > 0 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  marginTop: '2mm', borderTop: '1px dashed #ccc', paddingTop: '1mm',
                  color: '#dc2626', fontWeight: 'bold',
                }}>
                  <span>{t.balanceDue ?? 'Balance Due'}:</span>
                  <span>{fmt(data.balance_due)}</span>
                </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '10mm', fontSize: '10px' }}>
          <div>
            <span style={{ fontWeight: 'bold' }}>Authorized By: </span>{currentUser?.name ?? '—'}
          </div>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontWeight: 'bold' }}>Printed On: </span>
            {DateUtil.formatDateForType(printedOn, 'date-time')}
          </div>
        </div>

      </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function Row({ label, value }: { label: string; value: string }) {
  return (
      <div style={{ marginBottom: '2mm' }}>
        <span style={{ fontWeight: 'bold' }}>{label}: </span>{value}
      </div>
  );
}

function AmtRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2mm', color }}>
        <span>{label}:</span>
        <span style={{ fontWeight: '500' }}>{value}</span>
      </div>
  );
}